// Messenger — TanStack Query hooks. Near-realtime через refetchInterval
// (вебсокетов пока нет; polling достаточно для фазы 1).

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import { branchQueryKey, useBranchIds, useTenantId } from '@/lib/tenant-context';

import {
  getConversation,
  archiveConversation,
  markConversationGroup,
  setConversationBranch,
  getClientFeed,
  deleteSentMessage,
  getMessages,
  getSummary,
  linkConversation,
  listConversations,
  markRead,
  markUnread,
  sendClientReply,
  sendReply,
  shareSchedule,
  unarchiveConversation,
  linkConversationToEmployee,
  listEmployeeLinkCandidates,
  linkEmployeeConversations,
  unlinkConversation,
  type ClientFeed,
  type ClientThreadMessage,
  type ConversationListItem,
  type EmployeeChannelCandidate,
  type ListConversationsParams,
  type MessengerSummary,
  type ShareScheduleBody,
  type ShareScheduleResult,
  type ShareScheduleTarget,
  type ThreadMessage,
  type ReplyConversation,
  type DeletionOutcome,
} from '../api/messenger.api';

const LIST_POLL_MS = 7_000;
const THREAD_POLL_MS = 5_000;
const SUMMARY_POLL_MS = 30_000;

export function messengerKeys(tenantId: string | null) {
  const root = ['messenger', tenantId] as const;
  return {
    root,
    conversations: (params: ListConversationsParams) => [...root, 'conversations', params] as const,
    messages: (conversationId: string) => [...root, 'messages', conversationId] as const,
    clientMessages: (clientId: string) => [...root, 'client-messages', clientId] as const,
    summary: () => [...root, 'summary'] as const,
  };
}

export function useConversations(
  params: ListConversationsParams,
  /**
   * `enabled: false` — не ходить на сервер вовсе. Нужно там, где вызывающий
   * знает, что запрашивать нечего (диалог не привязан к клиенту), а хук
   * условно не вызовешь.
   */
  options: { enabled?: boolean } = {},
): UseQueryResult<ConversationListItem[]> {
  const tenantId = useTenantId();
  const keys = messengerKeys(tenantId);
  return useQuery({
    queryKey: keys.conversations(params),
    queryFn: () => listConversations(params),
    refetchInterval: LIST_POLL_MS,
    refetchOnWindowFocus: true,
    enabled: options.enabled ?? true,
  });
}

export function useMessages(conversationId: string | null): UseQueryResult<ThreadMessage[]> {
  const tenantId = useTenantId();
  const keys = messengerKeys(tenantId);
  return useQuery({
    queryKey: keys.messages(conversationId ?? '—'),
    queryFn: () => getMessages(conversationId as string),
    enabled: Boolean(conversationId),
    refetchInterval: conversationId ? THREAD_POLL_MS : false,
  });
}

/**
 * Один запрос на ленту клиента и канал ответа по умолчанию (план 152 C5).
 * Два хука ниже читают его разными `select` под ОДНИМ ключом — запрос уходит
 * один, и подпись канала обновляется тем же опросом, что приносит сообщение
 * клиента из другого канала.
 */
function clientFeedQuery(keys: ReturnType<typeof messengerKeys>, clientId: string | null) {
  return {
    queryKey: keys.clientMessages(clientId ?? '—'),
    queryFn: (): Promise<ClientFeed> => getClientFeed(clientId as string),
    enabled: Boolean(clientId),
    refetchInterval: clientId ? THREAD_POLL_MS : (false as const),
  };
}

/** Объединённая лента «Все мессенджеры» по клиенту (сообщения всех каналов). */
export function useClientMessages(clientId: string | null): UseQueryResult<ClientThreadMessage[]> {
  const tenantId = useTenantId();
  const keys = messengerKeys(tenantId);
  return useQuery({ ...clientFeedQuery(keys, clientId), select: (feed) => feed.items });
}

/** Канал, куда по умолчанию уйдёт ответ по клиенту, — как его выберет сервер. */
export function useClientReplyConversation(
  clientId: string | null,
): UseQueryResult<ReplyConversation | null> {
  const tenantId = useTenantId();
  const keys = messengerKeys(tenantId);
  return useQuery({
    ...clientFeedQuery(keys, clientId),
    select: (feed) => feed.replyConversation,
  });
}

/**
 * Ответ из ленты «Все мессенджеры». `conversationId` — диалог, выбранный в
 * «Ответ: канал ▾»; без него сервер отправит в канал по умолчанию.
 * `replyToMessageId` (М2) — цитируемое сообщение; допустимо только из того же
 * диалога, иначе бэкенд ответит 409 messenger.reply_cross_channel.
 */
export function useSendClientReply(
  clientId: string,
): UseMutationResult<ClientThreadMessage, unknown, SendClientReplyArgs> {
  const tenantId = useTenantId();
  const keys = messengerKeys(tenantId);
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: SendClientReplyArgs) =>
      sendClientReply(
        clientId,
        args.text,
        args.replyToMessageId ?? null,
        args.mediaId ?? null,
        args.conversationId ?? null,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.clientMessages(clientId) });
      void qc.invalidateQueries({ queryKey: [...keys.root, 'conversations'] });
    },
  });
}

export function useMessengerSummary(): UseQueryResult<MessengerSummary> {
  const tenantId = useTenantId();
  const keys = messengerKeys(tenantId);
  // Филиал В КЛЮЧЕ: при переключении в шапке бейдж обязан пересчитаться, а не
  // показывать число прошлого филиала до следующего опроса.
  const branchIds = useBranchIds();
  return useQuery({
    queryKey: [...keys.summary(), branchQueryKey(branchIds)],
    queryFn: () => getSummary(branchIds),
    refetchInterval: SUMMARY_POLL_MS,
  });
}

/** Текст ответа + (опционально) цитируемое сообщение. */
export interface SendReplyArgs {
  text: string;
  replyToMessageId?: string | null;
  /** Вложение, уже загруженное через uploadChatMedia (docs/service/56). */
  mediaId?: string | null;
}

/** Ответ по клиенту: плюс диалог, выбранный в «Ответ: канал ▾» (план 152 C5). */
export interface SendClientReplyArgs extends SendReplyArgs {
  conversationId?: string | null;
}

export function useSendReply(
  conversationId: string,
): UseMutationResult<ThreadMessage, unknown, SendReplyArgs> {
  const tenantId = useTenantId();
  const keys = messengerKeys(tenantId);
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: SendReplyArgs) =>
      sendReply(conversationId, args.text, args.replyToMessageId ?? null, args.mediaId ?? null),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.messages(conversationId) });
      void qc.invalidateQueries({ queryKey: [...keys.root, 'conversations'] });
    },
  });
}

/**
 * «Поделиться» занятиями/группами (план 132 §2.1–2.2): карточки с кнопками
 * «Записаться» уходят той же ручкой, что и обычный ответ — по диалогу или по
 * клиенту, в зависимости от того, что есть у композера.
 */
export function useShareSchedule(
  target: ShareScheduleTarget,
): UseMutationResult<ShareScheduleResult, unknown, ShareScheduleBody> {
  const tenantId = useTenantId();
  const keys = messengerKeys(tenantId);
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ShareScheduleBody) => shareSchedule(target, body),
    onSuccess: () => {
      if ('conversationId' in target) {
        void qc.invalidateQueries({ queryKey: keys.messages(target.conversationId) });
        // Из ленты «Все каналы» карточки уходят в ВЫБРАННЫЙ диалог (план 152
        // C5) — по его id, и объединённая лента обязана их показать сразу, а
        // не через опрос.
        void qc.invalidateQueries({ queryKey: [...keys.root, 'client-messages'] });
      } else {
        void qc.invalidateQueries({ queryKey: keys.clientMessages(target.clientId) });
      }
      void qc.invalidateQueries({ queryKey: [...keys.root, 'conversations'] });
    },
  });
}

/**
 * Отзыв отправленного. Инвалидируем и ленту диалога, и объединённую ленту
 * клиента: одно и то же сообщение видно в обоих местах.
 */
export function useDeleteSentMessage(
  conversationId: string | null,
): UseMutationResult<DeletionOutcome, unknown, string> {
  const tenantId = useTenantId();
  const keys = messengerKeys(tenantId);
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (messageId: string) => deleteSentMessage(messageId),
    onSuccess: () => {
      if (conversationId) {
        void qc.invalidateQueries({ queryKey: keys.messages(conversationId) });
      }
      void qc.invalidateQueries({ queryKey: [...keys.root, 'client-messages'] });
    },
  });
}

export function useLinkConversation(): UseMutationResult<
  ConversationListItem,
  unknown,
  { conversationId: string; clientId: string; confirmPhoneMismatch?: boolean }
> {
  const tenantId = useTenantId();
  const keys = messengerKeys(tenantId);
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ conversationId, clientId, confirmPhoneMismatch }) =>
      linkConversation(conversationId, clientId, confirmPhoneMismatch === true),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [...keys.root, 'conversations'] });
    },
  });
}

/** Привязать диалог к сотруднику (правка владельца 2026-07-30). */
export function useLinkConversationToEmployee(): UseMutationResult<
  ConversationListItem,
  unknown,
  { conversationId: string; employeeId: string }
> {
  const tenantId = useTenantId();
  const keys = messengerKeys(tenantId);
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args) => linkConversationToEmployee(args.conversationId, args.employeeId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.root });
    },
  });
}

/**
 * Каналы, похожие на диалоги этого сотрудника, но ещё ни к кому не привязанные
 * (план 107). Без polling: список меняется от действий человека, а не сам по
 * себе, и инвалидируется привязкой.
 */
export function useEmployeeLinkCandidates(
  employeeId: string | null,
  options?: { enabled?: boolean },
): UseQueryResult<EmployeeChannelCandidate[]> {
  const tenantId = useTenantId();
  const keys = messengerKeys(tenantId);
  return useQuery({
    queryKey: [...keys.root, 'employee-link-candidates', employeeId],
    queryFn: () => listEmployeeLinkCandidates(employeeId as string),
    enabled: Boolean(employeeId) && (options?.enabled ?? true),
  });
}

/** Привязать к сотруднику отмеченные каналы разом (план 107). */
export function useLinkEmployeeConversations(): UseMutationResult<
  { linked: string[]; skipped: string[] },
  unknown,
  { employeeId: string; conversationIds: string[] }
> {
  const tenantId = useTenantId();
  const keys = messengerKeys(tenantId);
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args) => linkEmployeeConversations(args.employeeId, args.conversationIds),
    onSuccess: () => {
      // Инвалидируем весь корень: привязка меняет и список диалогов, и
      // кандидатов, и каналы сотрудника — три разных ключа.
      void qc.invalidateQueries({ queryKey: keys.root });
    },
  });
}

export function useUnlinkConversation(): UseMutationResult<
  ConversationListItem,
  unknown,
  { conversationId: string; what?: 'client' | 'employee' | 'both' }
> {
  const tenantId = useTenantId();
  const keys = messengerKeys(tenantId);
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { conversationId: string; what?: 'client' | 'employee' | 'both' }) =>
      unlinkConversation(args.conversationId, args.what ?? 'client'),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [...keys.root, 'conversations'] });
    },
  });
}

/** В архив (М3): диалог уходит из рабочего списка, счётчик непрочитанных живёт. */
export function useArchiveConversation(): UseMutationResult<ConversationListItem, unknown, string> {
  const tenantId = useTenantId();
  const keys = messengerKeys(tenantId);
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (conversationId: string) => archiveConversation(conversationId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [...keys.root, 'conversations'] });
    },
  });
}

export function useUnarchiveConversation(): UseMutationResult<
  ConversationListItem,
  unknown,
  string
> {
  const tenantId = useTenantId();
  const keys = messengerKeys(tenantId);
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (conversationId: string) => unarchiveConversation(conversationId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [...keys.root, 'conversations'] });
    },
  });
}

/** Пометка «групповой чат» вручную (батч №23 П10). */
export function useMarkConversationGroup(): UseMutationResult<
  ConversationListItem,
  unknown,
  { conversationId: string; isGroup: boolean }
> {
  const tenantId = useTenantId();
  const keys = messengerKeys(tenantId);
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ conversationId, isGroup }) => markConversationGroup(conversationId, isGroup),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [...keys.root, 'conversations'] });
    },
  });
}

/** Локация диалога вручную (план 103). */
export function useSetConversationBranch(): UseMutationResult<
  ConversationListItem,
  unknown,
  { conversationId: string; branchId: string | null }
> {
  const tenantId = useTenantId();
  const keys = messengerKeys(tenantId);
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ conversationId, branchId }) => setConversationBranch(conversationId, branchId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [...keys.root, 'conversations'] });
    },
  });
}

export function useMarkRead(): UseMutationResult<void, unknown, string> {
  const tenantId = useTenantId();
  const keys = messengerKeys(tenantId);
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (conversationId: string) => markRead(conversationId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [...keys.root, 'conversations'] });
      void qc.invalidateQueries({ queryKey: keys.summary() });
    },
  });
}

export function useMarkUnread(): UseMutationResult<void, unknown, string> {
  const tenantId = useTenantId();
  const keys = messengerKeys(tenantId);
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (conversationId: string) => markUnread(conversationId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [...keys.root, 'conversations'] });
      void qc.invalidateQueries({ queryKey: keys.summary() });
    },
  });
}

/**
 * Заголовок одного диалога — для перехода по ссылке `?conversation=`.
 *
 * Зачем отдельно от списка: список отфильтрован филиалом из шапки, и диалога
 * из другого филиала в нём не будет вовсе. Без дозагрузки ссылка (например, из
 * окна «Написать первым», план 91) молча вела бы в пустоту.
 */
export function useConversation(
  conversationId: string | null,
  options?: { enabled?: boolean },
): UseQueryResult<ConversationListItem, Error> {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: [...messengerKeys(tenantId).root, 'one', conversationId],
    queryFn: () => getConversation(conversationId as string),
    enabled: Boolean(conversationId) && (options?.enabled ?? true),
    retry: false,
  });
}
